// src/pages/admin/AdminVecis.js

import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, TextField, Table, TableHead, TableRow, TableCell,
  TableBody, TablePagination, IconButton, Stack, Alert, CircularProgress, Tooltip, Dialog,
  DialogTitle, DialogContent, DialogActions, Button, Grid, Snackbar,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import KeyIcon        from '@mui/icons-material/Key';
import SearchIcon     from '@mui/icons-material/Search';
import ClearIcon      from '@mui/icons-material/Clear';
import { admin } from '../../api/authApi';
import ChipEstadoCuenta from '../../components/admin/ChipEstadoCuenta';
import DialogCambiarPassword from '../../components/admin/DialogCambiarPassword';
import { fechaHoraLocal } from '../../utils/format';
import {
  useFiltrosUbicacion, SelectorDepartamento, SelectorMunicipio,
} from '../../components/admin/FiltrosUbicacion';

export default function AdminVecis() {
  const [search, setSearch] = useState('');
  const [filtrosGeo, setFiltrosGeo] = useState({ departamentoId: '', municipioId: '' });
  const [page, setPage]     = useState(0);
  const [size, setSize]     = useState(20);

  const [data, setData] = useState({ total: 0, items: [] });
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState('');
  const [detalle, setDetalle] = useState(null);
  const [password, setPassword] = useState(null);   // null | veci
  const [aviso, setAviso]       = useState('');

  const geo = useFiltrosUbicacion({ cargarGuias: false });

  const limpiarFiltros = () => {
    setSearch('');
    setFiltrosGeo({ departamentoId: '', municipioId: '' });
    setPage(0);
  };
  const hayFiltros = !!(search || filtrosGeo.departamentoId || filtrosGeo.municipioId);

  const cargar = useCallback(async () => {
    setCargando(true); setError('');
    try {
      const r = await admin.vecisListar({
        search: search || undefined,
        departamentoId: filtrosGeo.departamentoId || undefined,
        municipioId:    filtrosGeo.municipioId    || undefined,
        page: page + 1, size,
      });
      setData({ total: r.total, items: r.items });
    } catch (e) {
      setError(e.mensajeUsuario || 'No se pudieron cargar los vecis.');
    } finally { setCargando(false); }
  }, [search, filtrosGeo, page, size]);

  useEffect(() => { cargar(); }, [cargar]);

  const abrirDetalle = async (id) => {
    try { setDetalle(await admin.veciDetalle(id)); }
    catch (e) { setError(e.mensajeUsuario || 'No se pudo cargar el detalle.'); }
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ fontFamily: 'Fraunces', mb: 3 }}>Vecis</Typography>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap"
                 useFlexGap sx={{ rowGap: 1.5 }}>
            <TextField
              label="Buscar (nombre, apodo, correo o teléfono)"
              value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }}
              size="small" sx={{ flex: '1 1 320px', minWidth: 320 }}
              InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} /> }}
            />
            <SelectorDepartamento geo={geo} valor={filtrosGeo.departamentoId}
              onChange={(v) => { setFiltrosGeo(f => ({ ...f, departamentoId: v, municipioId: '' })); setPage(0); }} />
            <SelectorMunicipio geo={geo} valor={filtrosGeo.municipioId}
              departamentoId={filtrosGeo.departamentoId}
              onChange={(v) => { setFiltrosGeo(f => ({ ...f, municipioId: v })); setPage(0); }} />
            {hayFiltros && (
              <Button size="small" startIcon={<ClearIcon />} onClick={limpiarFiltros}
                      sx={{ ml: 'auto' }}>
                Limpiar
              </Button>
            )}
          </Stack>
        </CardContent>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Card>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Código</TableCell>
              <TableCell>Nombre / Apodo</TableCell>
              <TableCell>Correo</TableCell>
              <TableCell>Teléfono</TableCell>
              <TableCell>Municipio</TableCell>
              <TableCell>Dirección</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell>Último acceso</TableCell>
              <TableCell align="right">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {cargando && <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4 }}><CircularProgress size={24} /></TableCell></TableRow>}
            {!cargando && data.items.length === 0 && (
              <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                Aún no hay vecis registrados.
              </TableCell></TableRow>)}
            {!cargando && data.items.map((v) => (
              <TableRow key={v.clienteId} hover>
                <TableCell><Typography variant="caption">{v.codigo}</Typography></TableCell>
                <TableCell>{v.apodo ? `${v.apodo} (${v.nombreCompleto})` : v.nombreCompleto}</TableCell>
                <TableCell>{v.email || '—'}</TableCell>
                <TableCell>{v.telefono || '—'}</TableCell>
                <TableCell>
                  {v.municipioNombre
                    ? <>
                        <Typography variant="body2">{v.municipioNombre}</Typography>
                        <Typography variant="caption" color="text.secondary">{v.departamentoNombre}</Typography>
                      </>
                    : '—'}
                </TableCell>
                <TableCell>{v.direccion || '—'}</TableCell>
                <TableCell>
                  <ChipEstadoCuenta
                    estado={v.estadoCuenta}
                    correoConfirmado={v.correoConfirmado}
                    bloqueado={v.bloqueado}
                  />
                </TableCell>
                <TableCell>{v.ultimoAcceso ? fechaHoraLocal(v.ultimoAcceso) : '—'}</TableCell>
                <TableCell align="right">
                  <Tooltip title="Ver detalle"><IconButton size="small" onClick={() => abrirDetalle(v.clienteId)}><VisibilityIcon fontSize="small" /></IconButton></Tooltip>
                  <Tooltip title={v.aspNetUserId ? 'Cambiar contraseña' : 'Sin usuario de acceso'}>
                    <span>
                      <IconButton size="small" disabled={!v.aspNetUserId} onClick={() => setPassword(v)}>
                        <KeyIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div" count={data.total} page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={size}
          onRowsPerPageChange={(e) => { setSize(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 20, 50]}
        />
      </Card>

      <Dialog open={!!detalle} onClose={() => setDetalle(null)} maxWidth="sm" fullWidth>
        {detalle && <>
          <DialogTitle>{detalle.apodo || `${detalle.primerNombre} ${detalle.primerApellido}`.trim()}</DialogTitle>
          <DialogContent dividers>
            <Field k="Código"     v={detalle.codigo} />
            <Field k="Nombre"     v={`${detalle.primerNombre || ''} ${detalle.primerApellido || ''}`.trim() || '—'} />
            <Field k="Apodo"      v={detalle.apodo || '—'} />
            <Field k="Correo"     v={detalle.email || '—'} />
            <Field k="Teléfono"   v={detalle.telefono || '—'} />
            <Field k="Celular"    v={detalle.celular || '—'} />
            <Field k="Dirección"  v={detalle.direccion || '—'} />
            <Field k="Documento"  v={detalle.numeroDocumento || '—'} />
            <Field k="Municipio"  v={detalle.municipioID ?? '—'} />
            <Field k="Activo"     v={detalle.activo ? 'Sí' : 'No'} />
            <Field k="Registrado" v={fechaHoraLocal(detalle.fechaCreacion)} />
            <Field k="Último acceso" v={detalle.vinculo?.UltimoAcceso ? fechaHoraLocal(detalle.vinculo.UltimoAcceso) : '—'} />
            {(detalle.latitud != null && detalle.longitud != null) && (
              <Stack direction="row" spacing={1} sx={{ py: 0.5 }}>
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 160 }}>Ubicación</Typography>
                <Typography
                  component="a"
                  href={`https://www.google.com/maps/?q=${detalle.latitud},${detalle.longitud}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="body2"
                  sx={{ color: 'primary.main', textDecoration: 'underline' }}
                >
                  Abrir en Google Maps ({detalle.latitud.toFixed(5)}, {detalle.longitud.toFixed(5)})
                </Typography>
              </Stack>
            )}
          </DialogContent>
          <DialogActions><Button onClick={() => setDetalle(null)}>Cerrar</Button></DialogActions>
        </>}
      </Dialog>

      <DialogCambiarPassword
        abierto={password !== null}
        aspNetUserId={password?.aspNetUserId}
        nombre={password?.nombreCompleto || password?.apodo}
        detalle={[password?.email, password?.telefono].filter(Boolean).join(' · ')}
        onCerrar={() => setPassword(null)}
        onCambiada={setAviso}
      />

      <Snackbar
        open={!!aviso} autoHideDuration={6000} onClose={() => setAviso('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setAviso('')}>{aviso}</Alert>
      </Snackbar>
    </Box>
  );
}

const Field = ({ k, v }) => (
  <Stack direction="row" spacing={1} sx={{ py: 0.5 }}>
    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 160 }}>{k}</Typography>
    <Typography variant="body2">{v}</Typography>
  </Stack>
);
